import { Grid, Typography } from "@mui/material";
import _ from "lodash";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import Button from "../../../framework/Button";
import Checkbox from "../../../framework/Checkbox";
import GlobalContext from "../../../framework/GlobalContext";
import NumberInput from "../../../framework/NumberInput";
import SelectInput from "../../../framework/SelectInput";
import Shortcut from "../../../framework/Shortcut";
import TextInput from "../../../framework/TextInput";
import { displayToast } from "../../../framework/toast/toastUtils";

import { getBankList } from "../../../services/bank-services";
import { saveCustomer } from "../../../services/customer-services";
import { listEntity } from "../../../services/entity-services";

import { stateCodes } from "../../../utils/constant";
import { formStateHandler, isValidPhoneNumber } from "../../../utils/utils";

import {
  billingRates,
  customerTypeOptions,
  discountStyleOptions,
  gstinTypeOptions,
  paymentModes,
  transformPayload,
} from "../CustomersConstants";
import {
  debounceBankName,
  debounceEntityData,
  debounceEntityName,
} from "../customerUtils";

import classes from "./index.module.css";

const AddCustomer = ({
  customerRowEditData,
  onSaveCustomer = () => {},
  onEscape = () => {},
}) => {
  const [formState, setFormState] = useState({
    patientType: customerTypeOptions[0].label,
    paymentMode: paymentModes[0].label,
  });
  const [errorStates, setErrorStates] = useState({});
  const [entityData, setEntityData] = useState({});
  const [customerData, setCustomerData] = useState({});
  const [entityNameOptions, setEntityNameOptions] = useState([]);
  const [bankOptions, setBankOptions] = useState([]);

  const withPhoneCap = ({ value }) => value.length <= 10;
  const withPincodeCap = ({ value }) => value.length <= 6;

  const saveCustomerRef = useRef();
  const customerNameRef = useRef("");
  const customerIdentifierRef = useRef("");
  const addressRef = useRef("");
  const areaRef = useRef("");
  const stateRef = useRef("");
  const bankRef = useRef("");
  const billToEntity = useRef("");
  const tds = useRef("");
  const contactNumberRef = useRef("");
  const landLineRef = useRef("");
  const emailRef = useRef("");
  const foodLicenseRef = useRef("");
  const drugLicenseRef = useRef("");
  const panNumberRef = useRef("");
  const gstinRef = useRef("");
  const gstinTypeRef = useRef("");
  const creditDaysRef = useRef("");
  const creditLimitRef = useRef("");
  const remarkRef = useRef("");
  const discountStyleRef = useRef("");
  const paymentMode = useRef("");
  const billingRate = useRef("");
  const customerTypeRef = useRef("");
  const pincodeRef = useRef("");
  const cityRef = useRef("");
  //const partentRef = useRef("");
  const entityRef = useRef("");

  const { setCurrentComponent } = useContext(GlobalContext);

  const validateForm = useCallback((fieldName, data) => {
    if (fieldName === "entityName" || !fieldName) {
      if (!data?.entityName) {
        entityRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please Select Entity",
        });
      }
    }

    if (fieldName === "patientName" || !fieldName) {
      if (!data?.patientName) {
        customerNameRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Customer Name",
        });
      }
    }

    if (
      fieldName === "patientPhone" ||
      fieldName === "landlineNumber" ||
      !fieldName
    ) {
      if (!data?.patientPhone && !data?.landlineNumber) {
        contactNumberRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Contact Number or Landline Number",
        });
      }
      if (data?.patientPhone && !isValidPhoneNumber(data.patientPhone)) {
        return displayToast({
          type: "error",
          title: "Please enter a valid 10 digit mobile number.",
        });
      }
    }

    if (fieldName === "patientAddress" || !fieldName) {
      if (!data?.patientAddress && data?.patientType === "B2B") {
        addressRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Address",
        });
      }
    }

    if (fieldName === "state" || !fieldName) {
      if (!data?.state && data?.patientType === "B2B") {
        stateRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please select state",
        });
      }
    }

    if (fieldName === "patientCity" || !fieldName) {
      if (!data?.patientCity && data?.patientType === "B2B") {
        cityRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter city",
        });
      }
    }

    if (fieldName === "patientPincode" || !fieldName) {
      if (!data?.patientPincode && data?.patientType === "B2B") {
        pincodeRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter pincode",
        });
      }
    }
    if (fieldName === "patientEmail" || !fieldName) {
      const regex =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (data.patientEmail && !regex.test(data.patientEmail)) {
        displayToast({
          type: "error",
          title: "Please enter a valid email.",
        });
        setErrorStates((prev) => {
          return { ...prev, patientEmail: true };
        });
        if (!fieldName) emailRef.current.focus({ preventScroll: true });
        return false;
      }
    }

    if (fieldName === "panNumber" || !fieldName) {
      if (!data?.panNumber && data?.patientType === "B2B") {
        panNumberRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Pan Number",
        });
      }
      if (data?.panNumber && data?.panNumber.length !== 10) {
        return displayToast({
          type: "error",
          title: "Pan Number should be exactly 10 characters.",
        });
      }
    }

    if (fieldName === "gstinType" || !fieldName) {
      if (!data?.gstinType && data?.patientType === "B2B") {
        gstinTypeRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter GSTIN Type",
        });
      }
    }

    if (fieldName === "gstin" || !fieldName) {
      if (!data?.gstin && data?.patientType === "B2B") {
        gstinRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter GSTIN",
        });
      }

      const regex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/;
      if (data?.gstin && !regex.test(data.gstin)) {
        displayToast({
          type: "error",
          title: "Please enter a valid GSTIN.",
        });
        setErrorStates((prev) => {
          return { ...prev, gstin: true };
        });
        return false;
      }
    }

    if (fieldName === "foodLicense" || !fieldName) {
      if (!data?.foodLicense && data?.patientType === "B2B") {
        foodLicenseRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Food License",
        });
      }
    }

    if (fieldName === "drugLicense" || !fieldName) {
      if (!data?.drugLicense && data?.patientType === "B2B") {
        drugLicenseRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Drug License",
        });
      }
    }

    if (fieldName === "creditDays" || !fieldName) {
      if (!data?.creditDays && data?.creditLimit) {
        creditDaysRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Credit Days",
        });
      }
    }

    if (fieldName === "creditLimit" || !fieldName) {
      if (!data?.creditLimit && data?.creditDays) {
        creditLimitRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter Credit Limit",
        });
      }
    }

    return true;
  }, []);

  const saveCustomerHandler = useCallback(
    async (e) => {
      try {
        if (!validateForm(null, formState)) return;
        const payload = transformPayload({ ...formState });
        const response = await saveCustomer(payload);
        if (response.status === "success") {
          onSaveCustomer(response);
          return displayToast({
            type: "success",
            title: "Customer added successfully.",
          });
        }
      } catch (error) {
        console.log(error);
      }
    },
    [formState, onSaveCustomer, validateForm]
  );

  useEffect(() => {
    if (formState?.billToEntity) {
      setFormState((prev) => {
        return { ...prev, ...entityData };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityData]);
  useEffect(() => {
    debounceEntityName(listEntity, "", setEntityNameOptions);
    debounceEntityData(
      listEntity,
      customerRowEditData?.entityId,
      setEntityData,
      setFormState
    );
    debounceBankName(getBankList, "", setBankOptions);
    if (!customerRowEditData) return;
    setFormState(() => {
      return {
        ...customerRowEditData,
        bankName: customerRowEditData?.patientBankData?.bankName,
        entityName: customerRowEditData?.entityData?.entityName,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        return onEscape();
      }
      if (event.ctrlKey && key === "s") {
        return saveCustomerRef.current.click();
      }
    },
    [onEscape]
  );
  const handleFormChange = useCallback(
    (fieldName, fieldValue, isSelect = false, options, sendOption = false) => {
      setFormState((formState) => {
        const updatedFormState = formStateHandler(
          formState,
          fieldName,
          fieldValue,
          isSelect,
          options,
          sendOption
        );
        return updatedFormState;
      });
    },
    []
  );

  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);

  const handleBillToEntityChange = () => {
    const billToEntity = !formState.billToEntity;
    if (billToEntity) {
      handleFormChange("paymentMode", null, true, "CREDIT", true);
      if (_.isEmpty(customerData)) setCustomerData(formState);
      setFormState((prev) => {
        return { ...prev, ...entityData };
      });
    } else {
      setFormState((prev) => {
        return {
          ...customerData,
          entityName: prev?.entityName,
          entityId: prev?.entityId,
          tds: prev?.tds,
          patientName: prev?.patientName,
          patientPhone: prev?.patientPhone,
          patientIdentifier: prev?.patientIdentifier,
          patientAddress: prev?.patientAddress,
          patientCity: prev?.patientCity,
          area: prev?.area,
          patientEmail: prev?.patientEmail,
          discountStyle: prev?.discountStyle,
          billingRate: prev?.billingRate,
          remarks: prev?.remarks,
        };
      });
      setCustomerData({});
    }
    handleFormChange("billToEntity", null, true, billToEntity, true);
  };

  return (
    <div>
      <div className={classes["customer-detail-label"]}>
        Customer Details
        <Typography fontSize={17} fontWeight={700}>
          Account Head: Assets/Current Assets/Sundry Debtors
        </Typography>
      </div>
      <div className={classes["customer-details-form"]}>
        <Grid container spacing={2} sx={{ p: "10px 30px" }}>
          <Grid item xs={4}>
            <SelectInput
              id="add_customer_entity"
              label="Family/Entity"
              ref={entityRef}
              disableClearable={false}
              options={entityNameOptions}
              getOptionLabel={(option) => option?.entityName || option}
              isOptionEqualToValue={(option, value) => {
                return option?.entityId === value?.entityId;
              }}
              noOptionsText="Search Family Here (e.g., Gupta)"
              name="entityName"
              onFocus={() => setCurrentComponent("add_customer_entity")}
              onChange={(e, option) => {
                debounceEntityData(
                  listEntity,
                  option?.entityId,
                  setEntityData,
                  setFormState
                );
                handleFormChange(
                  "entityName",
                  null,
                  true,
                  option?.entityName,
                  true
                );
                handleFormChange(
                  "entityId",
                  null,
                  true,
                  option?.entityId,
                  true
                );
              }}
              required
              value={formState?.entityName || ""}
              onInputChange={(e) => {
                if (!e) return;
                setEntityNameOptions([]);
                const value = e.target.value;
                if (value?.length > 0)
                  debounceEntityName(
                    listEntity,
                    value.trim(),
                    setEntityNameOptions
                  );
              }}
              markers={JSON.stringify({
                up: "add_customer_entity",
                left: "add_customer_entity",
                right: formState?.entityName
                  ? "add_customer_bill_entity"
                  : "add_customer_tds",
                down: "add_customer_contactnumber",
              })}
              autoFocus
            />
          </Grid>
          <Grid item xs={4}>
            <Grid container spacing={1}>
              <Grid
                item
                xs={6}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleBillToEntityChange();
                }}
              >
                <Checkbox
                  id="add_customer_bill_entity"
                  name="billToEntity"
                  ref={billToEntity}
                  label="Bill to Entity"
                  checked={formState?.billToEntity || false}
                  onFocus={() =>
                    setCurrentComponent("add_customer_bill_entity")
                  }
                  disabled={_.isEmpty(formState?.entityName)}
                  onChange={handleBillToEntityChange}
                  inputProps={{
                    "data-markers": JSON.stringify({
                      up: "add_customer_bill_entity",
                      down: "add_customer_contactnumber",
                      left: "add_customer_entity",
                      right: "add_customer_tds",
                    }),
                  }}
                />
              </Grid>

              <Grid
                item
                xs={6}
                onKeyDown={(e) => {
                  if (e.key === "Enter")
                    handleFormChange("tds", !formState.tds);
                }}
              >
                <Checkbox
                  id="add_customer_tds"
                  name="TDS"
                  ref={tds}
                  label="TDS"
                  checked={formState?.tds || false}
                  onFocus={() => setCurrentComponent("add_customer_tds")}
                  onChange={(event) => {
                    handleFormChange("tds", event.target.checked);
                  }}
                  inputProps={{
                    "data-markers": JSON.stringify({
                      up: "add_customer_tds",
                      down: "add_customer_contactnumber",
                      left: formState?.entityName
                        ? "add_customer_bill_entity"
                        : "add_customer_entity",
                      right: formState?.billToEntity
                        ? "add_customer_customername"
                        : "add_customer_customertype",
                    }),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4}>
            <SelectInput
              label="Customer Type"
              id="add_customer_customertype"
              ref={customerTypeRef}
              placeHolder="Customer Type"
              openOnFocus={true}
              name="patientType"
              onFocus={() => {
                setCurrentComponent("add_customer_customertype");
              }}
              onChange={(event, option) => {
                handleFormChange("patientType", null, true, option.label, true);
              }}
              isOptionEqualToValue={(option, value) => {
                return option.id === value.id;
              }}
              disabled={formState?.billToEntity}
              getOptionLabel={(option) => option.label || option}
              options={customerTypeOptions}
              value={formState?.patientType || ""}
              required={false}
              markers={JSON.stringify({
                up: "add_customer_customertype",
                left: "add_customer_tds",
                right: "add_customer_customername",
                down: "add_customer_customeridentifier",
              })}
            />
          </Grid>
        </Grid>

        {/* patientName->Customer Name, patientPhone->Contact Number, patientIdentifier->Customer Identifier
         */}

        <Grid container spacing={2} sx={{ p: "10px 30px" }}>
          <Grid item xs={4}>
            <TextInput
              id="add_customer_customername"
              ref={customerNameRef}
              name="patientName"
              label="Customer Name"
              required={true}
              onFocus={() => setCurrentComponent("add_customer_customername")}
              onChange={(e) => {
                handleFormChange("patientName", e.target.value);
                if (errorStates?.patientName)
                  setErrorStates((prev) => {
                    return { ...prev, patientName: false };
                  });
              }}
              value={formState?.patientName || ""}
              onBlur={() => {
                if (formState.patientName)
                  validateForm("patientName", formState);
              }}
              maxInputLength={10000}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_entity",
                  left: formState?.billToEntity
                    ? "add_customer_tds"
                    : "add_customer_customertype",
                  right: "add_customer_contactnumber",
                  down: "add_customer_address",
                }),
              }}
              error={errorStates?.patientName}
              // autoFocus={medicine ? true : false}
            />
          </Grid>
          <Grid item xs={4}>
            <NumberInput
              id="add_customer_contactnumber"
              ref={contactNumberRef}
              name="patientPhone"
              label="Contact Number"
              isAllowed={withPhoneCap}
              allowNegative={false}
              decimalScale={0}
              required={!formState?.landlineNumber}
              onFocus={() => setCurrentComponent("add_customer_contactnumber")}
              onValueChange={(values, e) => {
                handleFormChange("patientPhone", values.floatValue || null);
                if (errorStates?.patientPhone)
                  setErrorStates((prev) => {
                    return { ...prev, patientPhone: false };
                  });
              }}
              value={formState?.patientPhone || ""}
              onBlur={() => {
                if (formState.patientPhone)
                  validateForm("patientPhone", formState);
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_tds",
                  left: "add_customer_customername",
                  right: "add_customer_landlinenumber",
                  down: "add_customer_state",
                }),
              }}
              error={errorStates?.patientPhone}
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput
              id="add_customer_landlinenumber"
              ref={landLineRef}
              name="patientlandLine"
              label="Phone/Landline Number"
              onFocus={() => setCurrentComponent("add_customer_landlinenumber")}
              onChange={(e) => {
                if (e.target.value?.length > 20) return;
                const landlineRegex = /^[0-9]*$/;
                if (!landlineRegex.test(e.target.value)) {
                  return;
                }
                handleFormChange("landlineNumber", e.target.value || null);
                if (errorStates?.landlineNumber)
                  setErrorStates((prev) => {
                    return { ...prev, landlineNumber: false };
                  });
              }}
              value={formState?.landlineNumber || ""}
              onBlur={() => {
                if (formState.landlineNumber)
                  validateForm("landlineNumber", formState);
              }}
              required={!formState?.patientPhone}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_customertype",
                  left: "add_customer_contactnumber",
                  right: "add_customer_customeridentifier",
                  down: "add_customer_state",
                }),
              }}
              error={errorStates?.landlineNumber}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ p: "10px 30px" }}>
          <Grid item xs={4}>
            <TextInput
              id="add_customer_customeridentifier"
              ref={customerIdentifierRef}
              name="patientIdentifier"
              label="Customer Identifier"
              required={false}
              onFocus={() =>
                setCurrentComponent("add_customer_customeridentifier")
              }
              onChange={(e) => {
                handleFormChange("patientIdentifier", e.target.value);
                if (errorStates?.patientIdentifier)
                  setErrorStates((prev) => {
                    return { ...prev, patientIdentifier: false };
                  });
              }}
              value={formState?.patientIdentifier || ""}
              onBlur={() => {
                if (formState.patientIdentifier)
                  validateForm("patientIdentifier", formState);
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: formState?.billToEntity
                    ? "add_customer_customeridentifier"
                    : "add_customer_customertype",
                  left: "add_customer_landlinenumber",
                  right: "add_customer_address",
                  down: "add_customer_city",
                }),
              }}
              error={errorStates?.patientIdentifier}
              // autoFocus={medicine ? true : false}
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput
              id="add_customer_address"
              ref={addressRef}
              name="patientAddress"
              label="Address"
              onFocus={() => setCurrentComponent("add_customer_address")}
              onChange={(e) => {
                handleFormChange("patientAddress", e.target.value);
                if (errorStates?.patientAddress)
                  setErrorStates((prev) => {
                    return { ...prev, patientAddress: false };
                  });
              }}
              required={
                formState.billToEntity || formState.patientType === "B2B"
              }
              value={formState?.patientAddress || ""}
              onBlur={() => {
                if (formState.patientAddress)
                  validateForm("patientAddress", formState);
              }}
              maxInputLength={10000}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_customername",
                  left: "add_customer_customeridentifier",
                  right: "add_customer_state",
                  down: "add_customer_area",
                }),
              }}
              error={errorStates?.patientAddress}
              // autoFocus={medicine ? true : false}
            />
          </Grid>
          <Grid item xs={4}>
            <SelectInput
              key={formState.billToEntity}
              disableClearable={false}
              label="State"
              id="add_customer_state"
              ref={stateRef}
              placeHolder="State"
              openOnFocus={true}
              name="state"
              required={formState.patientType === "B2B"}
              onFocus={() => {
                setCurrentComponent("add_customer_state");
              }}
              onChange={(event, option) => {
                handleFormChange("state", null, true, option?.label, true);
              }}
              isOptionEqualToValue={(option, value) => {
                return option.id === value.id;
              }}
              //disabled={formState.billToEntity}
              getOptionLabel={(option) => option.label || option}
              options={stateCodes}
              value={formState?.state || ""}
              markers={JSON.stringify({
                up: "add_customer_contactnumber",
                left: "add_customer_address",
                right: "add_customer_city",
                down: "add_customer_pincode",
              })}
            />
          </Grid>
        </Grid>

        {/* area->Area, pincode->Pincode, patientEmail->Email
         */}

        <Grid container spacing={2} sx={{ p: "10px 30px" }}>
          <Grid item xs={4}>
            <TextInput
              id="add_customer_city"
              ref={cityRef}
              name="patientCity"
              label="City"
              onFocus={() => setCurrentComponent("add_customer_city")}
              onChange={(e) => {
                handleFormChange("patientCity", e.target.value);
                if (errorStates?.patientCity)
                  setErrorStates((prev) => {
                    return { ...prev, patientCity: false };
                  });
              }}
              required={formState.patientType === "B2B"}
              value={formState?.patientCity || ""}
              onBlur={() => {
                if (formState.patientCity)
                  validateForm("patientCity", formState);
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_customeridentifier",
                  left: "add_customer_state",
                  right: "add_customer_area",
                  down: "add_customer_email",
                }),
              }}
              error={errorStates?.area}
              //required={false}
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput
              id="add_customer_area"
              ref={areaRef}
              name="area"
              label="Area"
              onFocus={() => setCurrentComponent("add_customer_area")}
              onChange={(e) => {
                handleFormChange("area", e.target.value);
                if (errorStates?.area)
                  setErrorStates((prev) => {
                    return { ...prev, area: false };
                  });
              }}
              value={formState?.area || ""}
              onBlur={() => {
                if (formState.area) validateForm("area", formState);
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_address",
                  left: "add_customer_city",
                  right: "add_customer_pincode",
                  down: formState?.billToEntity
                    ? "add_customer_discountstyle"
                    : "add_customer_pannumber",
                }),
              }}
              error={errorStates?.area}
              required={false}
            />
          </Grid>
          <Grid item xs={4}>
            <NumberInput
              id="add_customer_pincode"
              ref={pincodeRef}
              name="patientPincode"
              label="Pincode"
              isAllowed={withPincodeCap}
              allowNegative={false}
              required={formState.patientType === "B2B"}
              //disabled={formState.billToEntity}
              onFocus={() => setCurrentComponent("add_customer_pincode")}
              onValueChange={(values, e) => {
                handleFormChange("patientPincode", values.formattedValue);
                if (errorStates?.patientPincode)
                  setErrorStates((prev) => {
                    return { ...prev, patientPincode: false };
                  });
              }}
              value={formState?.patientPincode || ""}
              onBlur={() => {
                if (formState.patientPincode)
                  validateForm("patientPincode", formState);
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_state",
                  left: "add_customer_area",
                  right: "add_customer_email",
                  down: formState?.billToEntity
                    ? "add_customer_remarks"
                    : "add_customer_gstintype",
                }),
              }}
              error={errorStates?.patientPincode}
            />
          </Grid>
        </Grid>

        {/* panNumber->Pan Number, gstinType->GSTIN Type, gstin->GSTIN
         */}

        <Grid container spacing={2} sx={{ p: "10px 30px" }}>
          <Grid item xs={4}>
            <TextInput
              id="add_customer_email"
              ref={emailRef}
              type="email"
              name="patientEmail"
              label="Email"
              required={false}
              onFocus={() => setCurrentComponent("add_customer_email")}
              onChange={(e) => {
                handleFormChange(
                  "patientEmail",
                  e.target.value.replace(/\s/g, "")
                );
                if (errorStates?.patientEmail)
                  setErrorStates((prev) => {
                    return { ...prev, patientEmail: false };
                  });
              }}
              value={formState?.patientEmail || ""}
              onBlur={() => {
                if (formState.patientEmail)
                  validateForm("patientEmail", formState);
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_city",
                  left: "add_customer_pincode",
                  right: formState?.billToEntity
                    ? "add_customer_discountstyle"
                    : "add_customer_pannumber",
                  down: formState?.billToEntity
                    ? "add_customer_email"
                    : "add_customer_gstin",
                }),
              }}
              error={errorStates?.patientEmail}
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput
              id="add_customer_pannumber"
              ref={panNumberRef}
              type="text"
              name="panNumber"
              label="Pan Number"
              required={formState.patientType === "B2B"}
              onFocus={() => setCurrentComponent("add_customer_pannumber")}
              onKeyDown={(e) => {
                if (e.key === " ") {
                  e.preventDefault();
                }
                if (!/^[A-Za-z0-9]*$/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                if (e.target.value?.length > 10) return;
                handleFormChange("panNumber", e.target.value.toUpperCase());
                if (errorStates?.panNumber)
                  setErrorStates((prev) => {
                    return { ...prev, panNumber: false };
                  });
              }}
              disabled={formState?.billToEntity}
              value={formState?.panNumber || ""}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_area",
                  left: "add_customer_email",
                  right: "add_customer_gstintype",
                  down: "add_customer_foodLicense",
                }),
              }}
              error={errorStates?.panNumber}
            />
          </Grid>
          <Grid item xs={4}>
            <SelectInput
              key={formState.billToEntity}
              label="GSTIN Type"
              id="add_customer_gstintype"
              ref={gstinTypeRef}
              placeHolder="GSTIN Type"
              openOnFocus={true}
              name="gstinType"
              required={formState.patientType === "B2B"}
              onFocus={() => {
                setCurrentComponent("add_customer_gstintype");
              }}
              onChange={(event, option) => {
                handleFormChange("gstinType", null, true, option?.label, true);
              }}
              onBlur={() => {
                if (formState.panNumber) validateForm("gstin", formState);
              }}
              isOptionEqualToValue={(option, value) => {
                return option.id === value.id;
              }}
              disabled={formState?.billToEntity}
              getOptionLabel={(option) => option?.label || option}
              options={gstinTypeOptions}
              value={formState?.gstinType || ""}
              markers={JSON.stringify({
                up: "add_customer_pincode",
                left: "add_customer_pannumber",
                right: "add_customer_gstin",
                down: "add_customer_drugLicense",
              })}
            />
          </Grid>
        </Grid>

        {/* foodLicense->Drug/Trade/Food License, discountStructure->Discount Structure, discountStyle->Discount Style
         */}

        <Grid container spacing={2} sx={{ p: "10px 30px" }}>
          <Grid item xs={4}>
            <TextInput
              id="add_customer_gstin"
              ref={gstinRef}
              type="text"
              name="gstin"
              label="GSTIN"
              required={formState.patientType === "B2B"}
              onFocus={() => setCurrentComponent("add_customer_gstin")}
              onChange={(e) => {
                handleFormChange("gstin", e.target.value.toUpperCase());
                if (errorStates?.gstin)
                  setErrorStates((prev) => {
                    return { ...prev, gstin: false };
                  });
              }}
              value={formState?.gstin || ""}
              disabled={formState?.billToEntity}
              onBlur={() => {
                if (formState.gstin) validateForm("gstin", formState);
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_email",
                  left: "add_customer_gstintype",
                  right: "add_customer_foodLicense",
                  down: "add_customer_bank_name",
                }),
              }}
              error={errorStates?.gstin}
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput
              id="add_customer_foodLicense"
              ref={foodLicenseRef}
              type="text"
              name="foodLicense"
              label="Food License"
              required={formState.patientType === "B2B"}
              onFocus={() => setCurrentComponent("add_customer_foodLicense")}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,14}$/.test(value)) {
                  handleFormChange("foodLicense", value);
                }
                //handleFormChange("foodLicense", e.target.value);
                if (errorStates?.foodLicense)
                  setErrorStates((prev) => {
                    return { ...prev, foodLicense: false };
                  });
              }}
              value={formState?.foodLicense || ""}
              onBlur={() => {
                if (formState.foodLicense)
                  validateForm("foodLicense", formState);
              }}
              disabled={formState?.billToEntity}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_pannumber",
                  left: "add_customer_gstin",
                  right: "add_customer_drugLicense",
                  down: "add_customer_discountstyle",
                }),
              }}
              error={errorStates?.foodLicense}
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput
              id="add_customer_drugLicense"
              ref={drugLicenseRef}
              type="text"
              name="drugLicense"
              label="Drug License"
              required={formState.patientType === "B2B"}
              onFocus={() => setCurrentComponent("add_customer_drugLicense")}
              onChange={(e) => {
                handleFormChange("drugLicense", e.target.value);
                if (errorStates?.drugLicense)
                  setErrorStates((prev) => {
                    return { ...prev, drugLicense: false };
                  });
              }}
              value={formState?.drugLicense || ""}
              onBlur={() => {
                if (formState.drugLicense)
                  validateForm("drugLicense", formState);
              }}
              disabled={formState?.billToEntity}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_gstintype",
                  left: "add_customer_foodLicense",
                  right: "add_customer_bank_name",
                  down: "add_customer_creditdays",
                }),
              }}
              error={errorStates?.foodLicense}
            />
          </Grid>
        </Grid>

        {/*  creditDays->Credit Days, creditLimit->Credit Limit, remarks->Remark
         */}

        <Grid container spacing={2} sx={{ p: "10px 30px" }}>
          <Grid item xs={4}>
            <SelectInput
              key={formState.billToEntity}
              label="Bank"
              id="add_customer_bank_name"
              ref={bankRef}
              openOnFocus={true}
              name="bankName"
              onFocus={() => {
                setCurrentComponent("add_customer_bank_name");
              }}
              onChange={(event, option) => {
                handleFormChange(
                  "bankName",
                  null,
                  true,
                  option?.bankName,
                  true
                );
                handleFormChange(
                  "patientBankId",
                  null,
                  true,
                  option?.bankId,
                  true
                );
              }}
              disableClearable={false}
              isOptionEqualToValue={(option, value) => {
                return option.bankId === value.bankId;
              }}
              options={bankOptions}
              value={formState?.bankName || ""}
              getOptionLabel={(option) => option?.bankName || option}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    {option.bankName} {option.accountNo}
                  </li>
                );
              }}
              required={false}
              disabled={formState?.billToEntity}
              markers={JSON.stringify({
                up: "add_customer_gstintype",
                left: "add_customer_drugLicense",
                right: "add_customer_discountstyle",
                down: "add_customer_creditlimit",
              })}
            />
          </Grid>
          <Grid item xs={4}>
            <SelectInput
              label="Discount Style"
              id="add_customer_discountstyle"
              ref={discountStyleRef}
              placeHolder="Discount Style"
              openOnFocus={true}
              name="discountStyle"
              onFocus={() => {
                setCurrentComponent("add_customer_discountstyle");
              }}
              onChange={(event, option) => {
                handleFormChange(
                  "discountStyle",
                  null,
                  true,
                  option?.label,
                  true
                );
              }}
              isOptionEqualToValue={(option, value) => {
                return option.id === value.id;
              }}
              getOptionLabel={(option) => option?.label || option}
              options={discountStyleOptions}
              value={formState?.discountStyle || ""}
              required={false}
              markers={JSON.stringify({
                up: "add_customer_gstin",
                left: formState?.billToEntity
                  ? "add_customer_email"
                  : "add_customer_bank_name",
                right: formState?.billToEntity
                  ? "add_customer_billingrate"
                  : "add_customer_creditdays",
                down: "add_customer_billingrate",
              })}
            />
          </Grid>
          <Grid item xs={4}>
            <NumberInput
              id="add_customer_creditdays"
              ref={creditDaysRef}
              name="creditDays"
              label="Credit Days"
              decimalScale={0}
              allowNegative={false}
              required={formState.creditLimit || formState.creditDays}
              onFocus={() => setCurrentComponent("add_customer_creditdays")}
              onValueChange={(values, e) => {
                handleFormChange("creditDays", values?.floatValue);
                if (errorStates?.creditDays)
                  setErrorStates((prev) => {
                    return { ...prev, creditDays: false };
                  });
              }}
              disabled={formState?.billToEntity}
              value={formState?.creditDays || ""}
              onBlur={() => {
                if (formState.creditDays) validateForm("creditDays", formState);
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_drugLicense",
                  left: "add_customer_discountstyle",
                  right: "add_customer_creditlimit",
                  down: "add_customer_remarks",
                }),
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ p: "10px 30px" }}>
          <Grid item xs={4}>
            <NumberInput
              id="add_customer_creditlimit"
              ref={creditLimitRef}
              name="creditLimit"
              label="Credit Limit"
              allowNegative={false}
              decimalScale={0}
              required={formState.creditDays || formState.creditLimit}
              onFocus={() => setCurrentComponent("add_customer_creditlimit")}
              onValueChange={(values, e) => {
                handleFormChange("creditLimit", values.floatValue);
                if (errorStates?.creditLimit)
                  setErrorStates((prev) => {
                    return { ...prev, creditLimit: false };
                  });
              }}
              disabled={formState?.billToEntity}
              value={formState?.creditLimit || ""}
              onBlur={() => {
                if (formState.creditLimit)
                  validateForm("creditLimit", formState);
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: "add_customer_bank_name",
                  left: "add_customer_creditdays",
                  right: "add_customer_billingrate",
                  down: "add_customer_paymentmodes",
                }),
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <SelectInput
              label="Billing Rate"
              id="add_customer_billingrate"
              ref={billingRate}
              placeHolder="Billing Rate"
              name="billingRate"
              onFocus={() => {
                setCurrentComponent("add_customer_billingrate");
              }}
              onChange={(event, option) => {
                handleFormChange("billingRate", option);
              }}
              isOptionEqualToValue={(option, value) => {
                return option === value;
              }}
              getOptionLabel={(option) => option}
              options={billingRates}
              value={formState?.billingRate || ""}
              required={false}
              markers={JSON.stringify({
                up: "add_customer_discountstyle",
                left: formState?.billToEntity
                  ? "add_customer_discountstyle"
                  : "add_customer_creditlimit",
                right: "add_customer_remarks",
                down: "add_customer_billingrate",
              })}
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput
              id="add_customer_remarks"
              ref={remarkRef}
              type="text"
              name="remarks"
              label="Remark"
              required={false}
              onFocus={() => setCurrentComponent("add_customer_remarks")}
              onChange={(e) => {
                handleFormChange("remarks", e.target.value);
                if (errorStates?.remarks)
                  setErrorStates((prev) => {
                    return { ...prev, remarks: false };
                  });
              }}
              value={formState?.remarks || ""}
              onBlur={() => {
                if (formState.remarks) validateForm("remarks", formState);
              }}
              maxInputLength={2000}
              inputProps={{
                "data-markers": JSON.stringify({
                  up: formState?.billToEntity
                    ? "add_customer_pincode"
                    : "add_customer_creditdays",
                  left: "add_customer_billingrate",
                  right: formState?.billToEntity
                    ? "add_customer_remarks"
                    : "add_customer_paymentmodes",
                  down: "add_customer_remarks",
                }),
              }}
              error={errorStates?.remarks}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ p: "10px 30px" }}>
          <Grid item xs={4}>
            <SelectInput
              label="Payment Modes"
              id="add_customer_paymentmodes"
              ref={paymentMode}
              placeHolder="Payment Modes"
              openOnFocus={true}
              name="paymentMode"
              onFocus={() => {
                setCurrentComponent("add_customer_paymentmodes");
              }}
              onChange={(event, option) => {
                handleFormChange("paymentMode", null, true, option.label, true);
              }}
              isOptionEqualToValue={(option, value) => {
                return option.id === value.id;
              }}
              disabled={formState?.billToEntity}
              getOptionLabel={(option) => option.label || option}
              options={paymentModes}
              value={formState?.paymentMode || ""}
              required={false}
              markers={JSON.stringify({
                up: "add_customer_creditlimit",
                left: "add_customer_remarks",
                right: "add_customer_paymentmodes",
                down: "add_customer_paymentmodes",
              })}
            />
          </Grid>
        </Grid>
      </div>

      <div className={classes["add-customer-save-button-wrapper"]}>
        <Button
          ref={saveCustomerRef}
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          label="Save Customer"
          color="violet"
          onClick={saveCustomerHandler}
        />
      </div>
    </div>
  );
};

export default AddCustomer;
